/**
 * L'appui long au doigt (ticket 12 du chantier `telephone`, décision d'Igor du 2026-09-26) :
 * un toucher entre dans la bulle, **un doigt gardé posé ouvre sa fiche**, partout où un
 * double-clic l'ouvre à la souris. On ne double-clique pas au doigt.
 *
 * Ce fichier ne fait que la règle, sans navigateur : ce qui arrive au doigt, et ce qu'il faut
 * en faire. `deuxGestes.ts` la branche sur les évènements pointeur.
 *
 * - Seul le doigt compte (`pointerType === 'touch'`) : la souris et le stylet gardent le clic
 *   et le double-clic, rien ne change pour eux.
 * - Le doigt posé arme le minuteur ; à `APPUI_LONG_MS`, s'il est toujours là, la fiche s'ouvre.
 * - Un doigt qui bouge de `TOLERANCE_PX` ou plus avant l'échéance annule l'appui : c'est un
 *   glisser (la carte se déplace dès 5 px, `cameraCarte.ts`) ou un défilement, pas un appui.
 * - Levé avant l'échéance : un toucher bref, le clic passe et fait entrer, comme avant.
 * - Levé après un appui long : le clic que le navigateur envoie au lâcher est avalé, **où
 *   qu'il tombe**. La fiche s'est ouverte sous le doigt : sans ça, le clic tombe sur elle
 *   (vu au ticket 12 en Liste, il changeait d'onglet et montrait la bulle d'à côté).
 */

/** Autour de 500 ms, l'usage des téléphones pour un appui long. */
export const APPUI_LONG_MS = 500;
/** Le seuil du glisser de la carte : au-delà, le doigt glisse, il n'appuie plus. */
export const TOLERANCE_PX = 5;
/**
 * Le clic du lâcher arrive quelques millisecondes après lui. Passé ce délai, on n'en attend
 * plus : un clic plus tardif est un vrai geste, il passe.
 */
export const CLIC_DU_LACHER_MS = 300;

export type EtatDAppui =
  | { phase: 'libre' }
  /** Un doigt posé, le minuteur court. */
  | { phase: 'pose'; x: number; y: number }
  /** La fiche est ouverte, le doigt toujours posé. */
  | { phase: 'tenu' };

export const LIBRE: EtatDAppui = { phase: 'libre' };

export type EvenementDAppui =
  | { type: 'pose'; pointerType: string; x: number; y: number }
  | { type: 'bouge'; x: number; y: number }
  | { type: 'echeance' }
  | { type: 'leve' }
  | { type: 'annule' };

export interface Suite {
  etat: EtatDAppui;
  /** Armer le minuteur de `APPUI_LONG_MS`, ou l'arrêter. */
  minuteur: 'armer' | 'arreter' | null;
  /** Ouvrir la fiche. */
  fiche: boolean;
  /** Avaler le clic du lâcher, dans les `CLIC_DU_LACHER_MS` qui viennent, où qu'il tombe. */
  avaler: boolean;
}

const rien = (etat: EtatDAppui): Suite => ({ etat, minuteur: null, fiche: false, avaler: false });

export function suivreLAppui(etat: EtatDAppui, evenement: EvenementDAppui): Suite {
  switch (evenement.type) {
    case 'pose':
      // Un nouveau pointeur efface tout ce qui restait du précédent.
      if (evenement.pointerType !== 'touch') {
        return { ...rien(LIBRE), minuteur: etat.phase === 'pose' ? 'arreter' : null };
      }
      return { ...rien({ phase: 'pose', x: evenement.x, y: evenement.y }), minuteur: 'armer' };
    case 'bouge':
      if (etat.phase !== 'pose') return rien(etat);
      if (Math.hypot(evenement.x - etat.x, evenement.y - etat.y) < TOLERANCE_PX) return rien(etat);
      return { ...rien(LIBRE), minuteur: 'arreter' };
    case 'echeance':
      if (etat.phase !== 'pose') return rien(etat);
      return { ...rien({ phase: 'tenu' }), fiche: true };
    case 'leve':
      // Levé à temps : un toucher bref, le clic qui suit fait entrer.
      if (etat.phase === 'pose') return { ...rien(LIBRE), minuteur: 'arreter' };
      if (etat.phase === 'tenu') return { ...rien(LIBRE), avaler: true };
      return rien(etat);
    case 'annule':
      // Le navigateur reprend le doigt (défilement) : ni fiche, ni clic à avaler.
      return { ...rien(LIBRE), minuteur: etat.phase === 'pose' ? 'arreter' : null };
  }
}
